import * as amplitude from "@amplitude/analytics-browser";
import { WarningTwoIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  HStack,
  Icon,
  Link,
  Spacer,
  Tag,
  TagLeftIcon,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { format, toZonedTime } from "date-fns-tz";
import { Fragment, type ReactNode, useCallback } from "react";
import { RiDeleteBin6Line, RiEditLine } from "react-icons/ri";
import { TbSun, TbSunOff } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { Card } from "src/components/Layout/Card";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { SearchAndFilterTypes } from "src/constants";
import { UseOrganizationType } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import {
  formatTimeWithTimeZone,
  isFutureExportTime,
  isTimeZoneUtc,
  REPEAT_MODE_OPTIONS,
} from "../../forms/export/dateUtils";
import { ScheduleConfig, ScheduleResponse } from "./types";

type ScheduledExportItemProps = {
  schedule: ScheduleResponse;
  organization: UseOrganizationType;
  onDelete: (pk: string) => void;
  isDeleting: boolean;
};

export const ScheduledExportItem = ({
  schedule,
  organization,
  onDelete,
  isDeleting,
}: ScheduledExportItemProps) => {
  const navigate = useNavigate();
  const timezoneName = organization
    .map((org) => org.timezone_name)
    .withDefault("");
  const exportName =
    schedule.exportName.length > 0 ? schedule.exportName : "form";
  const description = formatScheduleDescription(schedule, timezoneName);
  const editingDisabled =
    !schedule.exportTime.repeatMode &&
    !isFutureExportTime(schedule.exportTime.startTime);

  // Extract enrollment period ID from export config
  const exportConfigParams = new URLSearchParams(
    schedule.exportConfig.searchParamsString
  );
  const enrollmentPeriodId =
    exportConfigParams.get(SearchAndFilterTypes.EnrollmentPeriod) ?? null;

  // Get enrollment periods from provider
  const { enrollmentPeriods } = useEnrollmentPeriod();

  // Find matching enrollment period
  const enrollmentPeriod =
    enrollmentPeriods.hasData() && enrollmentPeriodId
      ? enrollmentPeriods.data.find((ep) => ep.id === enrollmentPeriodId) ??
        null
      : null;

  const handleEditClick = useCallback(() => {
    amplitude.track("Clicked edit scheduled export");
    const params = new URLSearchParams();
    // We need the enrollment period id to be set in the search params for the edit page to work, since
    // formTemplateId alone is not sufficient in the current form list navigation and context structure.
    if (enrollmentPeriodId) {
      params.set(SearchAndFilterTypes.EnrollmentPeriod, enrollmentPeriodId);
    }
    // Set scheduled export id so that the edit page can be loaded with the correct export config.
    params.set(SearchAndFilterTypes.ScheduledExportId, schedule.pk);
    navigate(
      organization
        .map(
          (org) =>
            `${Url.OrgAdmin.Forms.export({
              organization: org,
              formTemplateId: schedule.exportConfig.formTemplateId,
              params: params.toString(),
            })}`
        )
        .withDefault("#")
    );
  }, [navigate, organization, schedule, enrollmentPeriodId]);

  return (
    <Card
      display="flex"
      showBorder
      padding={6}
      width="100%"
      justifyContent="space-between"
    >
      <VStack width="100%" alignItems="start" gap={4}>
        {schedule.status === "FAILED" && (
          <Tag colorScheme="orange" size="sm">
            <TagLeftIcon as={WarningTwoIcon} />
            <Text>Not run</Text>
          </Tag>
        )}
        <HStack width="100%">
          <Text fontSize="lg" textColor="gray.800" fontWeight="600">
            {exportName}
          </Text>
          <Spacer />
          <Button
            variant={"ghost"}
            colorScheme="gray"
            size="sm"
            leftIcon={<RiDeleteBin6Line />}
            onClick={() => onDelete(schedule.pk)}
            isLoading={isDeleting}
          >
            Delete
          </Button>
          <Tooltip
            label={
              editingDisabled ? (
                <Text align="center">
                  This scheduled export cannot be edited because it is in the
                  past.
                </Text>
              ) : (
                ""
              )
            }
            hasArrow
            m={2}
          >
            <Button
              variant="outline"
              colorScheme="gray"
              size="sm"
              leftIcon={<RiEditLine />}
              onClick={handleEditClick}
              isDisabled={editingDisabled}
            >
              Edit
            </Button>
          </Tooltip>
        </HStack>
        {enrollmentPeriod && (
          <Text fontSize="sm" textColor="gray.600">
            Enrollment Period: {enrollmentPeriod.name}
          </Text>
        )}
        <Box textColor="gray.600" width="100%">
          {description}
        </Box>
      </VStack>
    </Card>
  );
};

function formatScheduleDescription(
  item: ScheduleResponse,
  timezoneName: string
) {
  const parts: ReactNode[] = [];
  const { exportTime: scheduleConfig, startTime, status, url } = item;

  // Show information about latest export for this schedule, if any
  if (status === "COMPLETED") {
    if (!url || !startTime) {
      throw new Error("Missing attributes on completed export");
    }
    const latestExportStartTime = toZonedTime(startTime, timezoneName);
    const isInCurrentYear =
      toZonedTime(new Date(), timezoneName).getFullYear() ===
      latestExportStartTime.getFullYear();
    const formattedTime = `${format(
      latestExportStartTime,
      isInCurrentYear ? "MMMM d" : "MMMM d, yyyy",
      { timeZone: timezoneName }
    )} at ${formatTimeWithTimeZone(
      latestExportStartTime,
      timezoneName,
      false
    )}`;
    if (scheduleConfig.repeatMode) {
      parts.push(`Last run on ${formattedTime}.`);
    } else {
      parts.push(`Ran on ${formattedTime}.`);
    }
  } else if (status === "FAILED") {
    parts.push("There was a problem with the export.");
  } else {
    parts.push("Never run.");
  }

  // Show details about the schedule config
  const scheduledTime = toZonedTime(scheduleConfig.startTime, timezoneName);
  if (scheduleConfig.repeatMode) {
    parts.push(
      `Runs ${REPEAT_MODE_OPTIONS[scheduleConfig.repeatMode].formatDescription(
        scheduledTime,
        timezoneName,
        !isTimeZoneUtc(scheduleConfig.timeZone)
      )}.`
    );
    parts.push(<ScheduleDstIndicator scheduleConfig={scheduleConfig} />);
  } else if (!status) {
    // one-time scheduled export not completed yet
    parts.push(
      `Scheduled for ${format(scheduledTime, "MMMM d, yyyy 'at' h:mmaaa zzz", {
        timeZone: timezoneName,
      })}.`
    );
  }
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 ? " " : null}
          {p}
        </Fragment>
      ))}
      {/* Add download link if available */}
      {url && (
        <>
          {" "}
          <Link
            href={url}
            textColor="primary.500"
            textDecoration="underline"
            onClick={() => amplitude.track("Downloaded scheduled export")}
          >
            Download
          </Link>
        </>
      )}
    </>
  );
}

const ScheduleDstIndicator = ({
  scheduleConfig,
}: {
  scheduleConfig: ScheduleConfig;
}) => {
  if (!scheduleConfig.repeatMode) {
    return null;
  }
  const isUtc = isTimeZoneUtc(scheduleConfig.timeZone);
  const label = isUtc
    ? `This export will not adjust for daylight saving time; it is scheduled at a fixed offset from UTC. You can change this by editing the export.`
    : "This export will adjust for daylight saving time in your organization's timezone. You can change this by editing the export.";
  return (
    <Tooltip label={label} hasArrow maxW="sm">
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        cursor="help"
        verticalAlign="middle"
        role="img"
        aria-label={
          isUtc
            ? "Does not adjust for daylight saving time"
            : "Adjusts for daylight saving time"
        }
      >
        <Icon as={isUtc ? TbSunOff : TbSun} boxSize={5} />
      </Box>
    </Tooltip>
  );
};
